import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Modal, Tag, TextArea, TextInput } from '@carbon/react';
import type { ColumnDef } from '@tanstack/react-table';
import { api } from '../api/client';
import { DataTable } from '../components/DataTable';
import { InlineError, PageHeader } from '../components/states';
import { fromZoneInput, toZoneInput } from '@ruby-cms/shared';
import { When } from '../components/format';
import { useAuth } from '../auth/AuthContext';
import { SegmentSelect } from './Segments';

type Msg = {
  id: string; title: string; body: string; gameUserId?: number | null; segmentId?: string | null; recipients?: number | null; createdAt: string;
  expiresAt?: string | null; recalledAt?: string | null; reads: number;
};
type Page = { items: Msg[] };

const stateOf = (m: Msg): [string, 'green' | 'gray' | 'red'] =>
  m.recalledAt ? ['Đã thu hồi', 'red'] : m.expiresAt && Date.parse(m.expiresAt) <= Date.now() ? ['Hết hạn', 'gray'] : ['Đang hiện', 'green'];

const toText = (m: Msg) => m.segmentId ? `Phân khúc, ${(m.recipients ?? 0).toLocaleString('vi-VN')} người` : m.gameUserId == null ? 'Tất cả' : '#' + m.gameUserId;

const columns: ColumnDef<Msg, any>[] = [
  { header: 'Tiêu đề', accessorKey: 'title' },
  { header: 'Người nhận', id: 'to', cell: (c) => toText(c.row.original) },
  { header: 'Đã mở', accessorKey: 'reads', cell: (c) => Number(c.getValue()).toLocaleString('vi-VN') + ' người' },
  { header: 'Gửi lúc', accessorKey: 'createdAt', cell: (c) => <When v={c.getValue()} /> },
  { header: 'Hết hạn', accessorKey: 'expiresAt', cell: (c) => c.getValue() ? <When v={c.getValue()} /> : 'Không' },
  { header: 'Trạng thái', id: 'st', cell: (c) => { const [t, k] = stateOf(c.row.original); return <Tag size="sm" type={k}>{t}</Tag>; } },
];

const localIso = (s?: string | null) => {
  if (!s) return '';
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? '' : toZoneInput(d); // the time of the CMS time zone
};

export function InboxPage() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['inbox'], queryFn: () => api.get<Page>('/api/admin/inbox') });
  const done = () => qc.invalidateQueries({ queryKey: ['inbox'] });
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [uid, setUid] = useState('');
  const [expires, setExpires] = useState('');
  const [seg, setSeg] = useState('');
  const { can } = useAuth();
  const create = useMutation({
    mutationFn: () => api.post('/api/admin/inbox', {
      title, body, ...(seg ? { segmentId: seg } : { gameUserId: uid ? Number(uid) : null }), ...(expires ? { expiresAt: fromZoneInput(expires)?.toISOString() } : {}),
    }),
    onSuccess: () => { setOpen(false); setTitle(''); setBody(''); setUid(''); setSeg(''); setExpires(''); done(); },
  });

  // one message: change its expiry or recall it
  const [pick, setPick] = useState<Msg | null>(null);
  const [eExp, setEExp] = useState('');
  const [confirm, setConfirm] = useState(false);
  const saveExp = useMutation({
    mutationFn: () => api.patch('/api/admin/inbox/' + pick!.id, { expiresAt: eExp ? fromZoneInput(eExp)?.toISOString() ?? null : null }),
    onSuccess: () => { setPick(null); done(); },
  });
  const recall = useMutation({
    mutationFn: () => api.post('/api/admin/inbox/' + pick!.id + '/recall'),
    onSuccess: () => { setConfirm(false); setPick(null); done(); },
  });

  return (
    <div className="page">
      <PageHeader title="Hộp thư" lead="Tin nhắn trong game. Để trống người nhận = gửi tất cả, hoặc chọn một phân khúc. Thu hồi thì thư biến khỏi hộp thư của mọi người chơi ngay."
        actions={<Button size="sm" onClick={() => setOpen(true)}>Gửi</Button>} />
      <DataTable columns={columns} data={q.data?.items} loading={q.isLoading} error={q.error} onRetry={() => q.refetch()}
        onRowClick={(m) => { setPick(m); setEExp(localIso(m.expiresAt)); }} rowKey={(m) => m.id}
        empty={{ title: 'Chưa có tin', body: 'Gửi tin tới một user hoặc cả sảnh.' }} />

      <Modal open={!!pick && !confirm} modalHeading={pick?.title ?? ''} primaryButtonText="Lưu hạn" secondaryButtonText="Đóng"
        primaryButtonDisabled={!!pick?.recalledAt || saveExp.isPending} onRequestClose={() => setPick(null)} onRequestSubmit={() => saveExp.mutate()}>
        {pick ? (
          <div className="stack">
            <p className="pre">{pick.body}</p>
            <p>{pick.segmentId ? toText(pick) : pick.gameUserId == null ? 'Gửi tất cả' : 'Gửi người chơi #' + pick.gameUserId} · {pick.reads.toLocaleString('vi-VN')} người đã mở · {stateOf(pick)[0]}</p>
            {pick.recalledAt ? <p>Thu hồi lúc <When v={pick.recalledAt} /></p> : (
              <>
                <TextInput id="in-eexp" type="datetime-local" labelText="Hết hạn (để trống = không hết hạn)" value={eExp} onChange={(e) => setEExp(e.target.value)} />
                <div className="row"><Button size="sm" kind="danger--tertiary" onClick={() => setConfirm(true)}>Thu hồi thư</Button></div>
              </>
            )}
            <InlineError error={saveExp.error} />
          </div>
        ) : null}
      </Modal>

      <Modal open={confirm} danger modalHeading={'Thu hồi ' + (pick?.title ?? '')} primaryButtonText="Thu hồi" secondaryButtonText="Hủy"
        primaryButtonDisabled={recall.isPending} onRequestClose={() => setConfirm(false)} onRequestSubmit={() => recall.mutate()}>
        <p>Thư biến khỏi hộp thư của mọi người chơi, kể cả người đã đọc. Không hoàn tác được.</p>
        <InlineError error={recall.error} />
      </Modal>

      <Modal open={open} modalHeading="Gửi hộp thư" primaryButtonText="Gửi" secondaryButtonText="Hủy"
        primaryButtonDisabled={!title || !body || create.isPending} onRequestClose={() => setOpen(false)} onRequestSubmit={() => create.mutate()}>
        <div className="stack">
          <TextInput id="in-t" labelText="Tiêu đề" value={title} onChange={(e) => setTitle(e.target.value)} />
          <TextArea id="in-b" labelText="Nội dung" value={body} onChange={(e) => setBody(e.target.value)} rows={5} />
          {can('crm.manage') ? <SegmentSelect id="in-seg" value={seg} onChange={setSeg} helperText="Gửi tới người chơi đang thuộc phân khúc lúc bấm Gửi." /> : null}
          {seg ? null : <TextInput id="in-u" labelText="Mã người chơi (trống = tất cả)" value={uid} onChange={(e) => setUid(e.target.value.replace(/\D/g, ''))} />}
          <TextInput id="in-exp" type="datetime-local" labelText="Hết hạn (để trống = không hết hạn)" value={expires} onChange={(e) => setExpires(e.target.value)} />
          <InlineError error={create.error} />
        </div>
      </Modal>
    </div>
  );
}
